import { usePrefetchQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { siteUnitClient } from '@portal-clients/site-unit';

import { useClientQuery } from '@frontend/hooks';
import { useOptionalUser } from '@frontend/utils';

import { sortSiteUnitsFavoritesFirst } from './sort-site-units-favorites-first';
import { getSiteUnitQueryKey } from './use-site-unit-query';

export const SITE_UNITS_QUERY_KEY = 'site-units';

const fetchSiteUnits = async () => {
  const response = await siteUnitClient.search();

  return response;
};

export const useSiteUnitsQuery = () => {
  const { user } = useOptionalUser();

  const query = useClientQuery({
    queryKey: [SITE_UNITS_QUERY_KEY],
    onFetch: async () => {
      return await fetchSiteUnits();
    },
  });

  const data = useMemo(() => {
    if (!query.data) {
      return query.data;
    }

    return sortSiteUnitsFavoritesFirst(query.data, user?.preferences?.sites);
  }, [query.data, user?.preferences?.sites]);

  return {
    ...query,
    data,
  };
};

export const usePrefetchSiteUnits = () => {
  const { user } = useOptionalUser();
  const queryClient = useQueryClient();

  usePrefetchQuery({
    queryKey: [SITE_UNITS_QUERY_KEY],
    queryFn: async () => {
      const response = await fetchSiteUnits();

      if (user?.preferences?.sites) {
        const favourites = response.filter((siteUnit) => {
          return user.preferences!.sites.includes(siteUnit.id);
        });

        for (const siteUnit of favourites) {
          queryClient.prefetchQuery({
            queryKey: getSiteUnitQueryKey(siteUnit.id),
            queryFn: async () => {
              return await siteUnitClient.get(siteUnit.id);
            },
          });
        }
      }

      return response;
    },
  });
};
